  
/* eslint-disable no-undef */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { BASE_URL } from '../api';
import OrdersTable from './OrdersTable';
import { CopyOutlined } from '@ant-design/icons';
import { openOrderSuccess } from './OrderSuccessModal';
import { cleanBybitFromMessage } from '../utils/messageUtils';

const StopLossPopup = ({ visible, order, currentPrice, onClose, fetchOrders }) => {
  const [slForm, setSlForm] = useState({ qty: '', price: '', orderType: 'Market' });
  const [slLoading, setSlLoading] = useState(false);
  const [livePrice, setLivePrice] = useState(currentPrice || '');
  const [slOrders, setSlOrders] = useState([]);
  const [ordersTableRefreshKey, setOrdersTableRefreshKey] = useState(0);
  const [qtyValidationMessage, setQtyValidationMessage] = useState('');
  
  // Add confirmation popup state
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (visible && order && order._id) {
      fetchSlOrders(order._id).then(() => {
        const { onHold, sold } = getSlQuantities();
        const filledQty = order ? (parseFloat(order.filledQuantity) || parseFloat(order.count) || 0) : 0;
        const maxQty = Math.max(0, filledQty - onHold - sold);
        setSlForm(prev => ({ ...prev, qty: maxQty.toString() }));
      });
    }
  }, [visible, order]);

  useEffect(() => {
    let interval;
    async function fetchPrice() {
      if (order && order.token) {
        try {
          const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${order.token}`);
          const data = await res.json();
          setLivePrice(data?.result?.list?.[0]?.markPrice || '');
        } catch {
          setLivePrice('');
        }
      }
    }
    if (visible && order && order.token) {
      fetchPrice();
      interval = setInterval(fetchPrice, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [visible, order]);

  // Reset form fields when popup is opened
  useEffect(() => {
    if (visible) {
      setSlForm({ qty: '', price: '', orderType: 'Market' });
      setQtyValidationMessage('');
    }
  }, [visible]);

  const fetchSlOrders = async (LinkedOrderId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/orders?orderNature=sl&orderID=${LinkedOrderId}`, { credentials: 'include' });
      const data = await res.json();
      setSlOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setSlOrders([]);
    }
  };

  // Calculate Quantity on hold and Sold quantity
  const getSlQuantities = () => {
    let onHold = 0;
    let sold = 0;
    // console.log(slOrders);
    slOrders.forEach(sl => {
      const status = (sl.orderStatus || '').toLowerCase();
      const qty = parseFloat(sl.count) || 0;
      // if (status !== 'cancelled' && status !== 'deactivated' && status !== 'order not placed' && status !== 'ordernotplaced') {
      //   onHold += qty;
      // }
       if ( status === 'untriggered' || status === 'new' || status ==='placed') {
        onHold += qty;
      }
      if (status === 'activated' || status === 'filled' || status === 'triggered') {
        sold += qty;
      }
    });
    return { onHold, sold };
  };
  
  const { onHold, sold } = getSlQuantities();
  const filledQty = order ? (parseFloat(order.filledQuantity) || parseFloat(order.count) || 0) : 0;
  const maxQty = Math.max(0, filledQty - onHold - sold);

  // Handle close all orders with confirmation
  const handleCloseAllOrders = async () => {
    if (!order || !order._id) return;
    try {
      const res = await fetch(`${BASE_URL}/api/orders/cancel-all/${order._id}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('All related orders cancelled!', { position: 'top-right', autoClose: 5000 });
        fetchOrders();
        setOrdersTableRefreshKey(prev => prev + 1);
      } else {
        toast.error(data.error || 'Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
      }
    } catch (err) {
      toast.error('Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
    } finally {
      setShowConfirmation(false);
    }
  };
  // Refresh SL orders whenever refreshKey changes (after cancel or submit)
  useEffect(() => {
    if (order && order._id) {
      fetchSlOrders(order._id);
    }
  }, [ordersTableRefreshKey, order]);

  // Verify stop loss price based on order side
  const stopLossVerifier = (currentPrice, inputPrice, side) => {
    if (!currentPrice || !inputPrice) return true;
    
    const current = parseFloat(currentPrice);
    const input = parseFloat(inputPrice);
    
    if (side === 'Buy') { // For Long position
      // Check if stop loss is above current price
      if (input > current) {
        toast.error('For Long position, stop loss cannot be above current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
      // Check if stop loss is too low (more than 75% below)
      const minAllowedPrice = current * 0.25;
      if (input < minAllowedPrice) {
        toast.error('For Long position, stop loss cannot be more than 75% below current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
    } else if (side === 'Sell') { // For Short position
      // Check if stop loss is below current price
      if (input < current) {
        toast.error('For Short position, stop loss cannot be below current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
      // Check if stop loss is too high (more than 75% above)
      const maxAllowedPrice = current * 1.75;
      if (input > maxAllowedPrice) {
        toast.error('For Short position, stop loss cannot be more than 75% above current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
    }
    return true;
  };

  // --- normalizeQuantity.js logic (inline for browser use) ---
  function countDecimals(str) {
    const s = String(str);
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : (s.length - dot - 1);
  }
  function toScaledBigInt(value, scale) {
    const s = String(value);
    const decs = countDecimals(s);
    if (decs === scale) return BigInt(s.replace(".", ""));
    if (decs < scale) {
      const zeros = "0".repeat(scale - decs);
      return BigInt((s.includes(".") ? s.replace(".", "") : s) + zeros);
    }
    const [intPart, fracPart = ""] = s.split(".");
    const keep = fracPart.slice(0, scale);
    const drop = fracPart.slice(scale);
    let core = BigInt((intPart || "0") + (keep || "").padEnd(scale, "0"));
    if (drop && /[1-9]/.test(drop)) {
      core += 1n;
    }
    return core;
  }
  function ceilToStep(n, step) {
    return ((n + (step - 1n)) / step) * step;
  }
  async function normalizeQuantity(symbol, desiredQuantity) {
    if (!symbol || desiredQuantity == null) {
      throw new Error("symbol and desiredQuantity are required.");
    }
    const url = new URL("https://api.bybit.com/v5/market/instruments-info");
    url.searchParams.set("category", "linear");
    url.searchParams.set("symbol", symbol);
    let data;
    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      data = await res.json();
    } catch (err) {
      throw new Error(`Failed to fetch Bybit instruments info: ${err.message}`);
    }
    if (data?.retCode !== 0 || !data?.result?.list?.length) {
      throw new Error(`Unexpected API response: retCode=${data?.retCode}`);
    }
    const lot = data.result.list[0]?.lotSizeFilter;
    if (!lot) throw new Error("lotSizeFilter not found for symbol.");
    const minOrderQtyStr = lot.minOrderQty;
    const qtyStepStr     = lot.qtyStep;
    if (minOrderQtyStr == null || qtyStepStr == null) {
      throw new Error("minOrderQty or qtyStep missing in lotSizeFilter.");
    }
    const decMin  = countDecimals(minOrderQtyStr);
    const decStep = countDecimals(qtyStepStr);
    const scale   = Math.max(decMin, decStep);
    const stepScaled = toScaledBigInt(qtyStepStr, scale);
    if (stepScaled <= 0n) throw new Error("Invalid qtyStep <= 0.");
    const minScaled = toScaledBigInt(minOrderQtyStr, scale);
    let desiredScaled = toScaledBigInt(desiredQuantity, scale);
    let adjustedScaled = ceilToStep(desiredScaled, stepScaled);
    const minAligned = ceilToStep(minScaled, stepScaled);
    if (adjustedScaled < minAligned) adjustedScaled = minAligned;
    const adjustedStr = adjustedScaled.toString();
    let result;
    if (scale === 0) {
      result = Number(adjustedStr);
    } else {
      const intPart = adjustedStr.slice(0, -scale) || "0";
      const fracPart = adjustedStr.slice(-scale).padStart(scale, "0");
      result = Number(`${intPart}.${fracPart}`);
    }
    return result;
  }

  const handleSlInput = async (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'qty') {
      // Clear any previous validation message
      setQtyValidationMessage('');
      if (val && (parseFloat(val) > maxQty)) {
        toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
        val = maxQty;
      }
    }
    setSlForm(prev => ({ ...prev, [name]: val }));
  };  // Small inline spinner for inner-button loading
  const SpinnerSmall = ({ size = 16, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: 'inline-block' }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray="31.415,31.415" transform="rotate(-90 25 25)">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
      </circle>
    </svg>
  );

  const handleSlSubmit = async () => {
    if (!slForm.qty || !slForm.price) return;
    if (parseFloat(slForm.qty) === 0) {
      toast.error("Zero quantity not available to process", { position: 'top-right', autoClose: 5000 });
      return;
    }
    if (parseFloat(slForm.qty) > maxQty) {
      toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
      return;
    }

    // Check for existing untriggered SL orders
    const existingUntriggeredSL = slOrders.find(order => 
      order.orderStatus && order.orderStatus.toLowerCase() === 'untriggered'
    );

    if (existingUntriggeredSL) {
      toast.error("There is already an active untriggered Stop Loss order. Please cancel it first.", { 
        position: 'top-right', 
        autoClose: 5000 
      });
      return;
    }

    // Verify stop loss price before submitting
    if (!stopLossVerifier(livePrice, slForm.price, order.side)) {
      return;
    }

    // Normalize quantity on submit
    let normalizedQty = slForm.qty;
    try {
      if (order && order.token) {
        normalizedQty = await normalizeQuantity(order.token, slForm.qty);
        
        // Check if normalized quantity is different from entered quantity
        if (parseFloat(normalizedQty) !== parseFloat(slForm.qty)) {
          setQtyValidationMessage(`System suggestion:${slForm.qty} is not valid. Minimum quantity step rounded to: ${normalizedQty}`);
          setSlForm(prev => ({ ...prev, qty: normalizedQty.toString() }));
          return;
        }
      }
    } catch (err) {
      toast.error('Error normalizing quantity: ' + err.message);
      return;
    }

    setSlLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: order.token,
          count: normalizedQty,
          orderType: 'Market',
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          algorithm: order.Algorithm,
          price: slForm.price,
          LinkedOrderId: order._id,
          orderNature: 'sl',
          positionIdx: order.positionIdx,
          stopLoss: slForm.price,
        }),
        credentials: 'include',
      });
      const data = await res.json();
      let placedOrderId = null;
      if (data.order && data.order.orderId) {
        placedOrderId = data.order.orderId;
      } else if (data.orderId) {
        placedOrderId = data.orderId;
      }
      if (data.bybitRes && data.bybitRes.message) {
        if (data.bybitRes.message === 'OK') {
          toast.success('Stop Loss order placed successfully!', { position: 'top-right', autoClose: 5000 });
          // Show order success modal with all details
          openOrderSuccess({
            orderId: data._id,
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy', // SL is opposite of original order
            positionIdx: order.positionIdx,
            isSuccess: true
          });
        } else if (data.bybitRes.message.includes('trigger')) {
          toast.error('Error code 101 -  please validate price input value and use close button option', { position: 'top-right', autoClose: 5000 });
          // Show order failure modal
          openOrderSuccess({
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy',
            positionIdx: order.positionIdx,
            isSuccess: false,
            errorMessage: 'Error code 101 - please validate price input value and use close button option'
          });
        } else {
          const cleanedMessage = cleanBybitFromMessage(data.bybitRes.message) || 'Stop Loss order failed!';
          toast.error(cleanedMessage, { position: 'top-right', autoClose: 5000 });
          // Show order failure modal
          openOrderSuccess({
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy',
            positionIdx: order.positionIdx,
            isSuccess: false,
            errorMessage: cleanedMessage
          });
        }
      } else if (data.success) {
        toast.success('Stop Loss order placed successfully!', { position: 'top-right', autoClose: 5000 });
        // Show order success modal with all details
        openOrderSuccess({
          orderId: data._id,
          symbol: order.token,
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          positionIdx: order.positionIdx,
          isSuccess: true
        });
      } else {
        const cleanedMessage = cleanBybitFromMessage(data.error) || 'Stop Loss order failed!';
        toast.error(cleanedMessage, { position: 'top-right', autoClose: 5000 });
        // Show order failure modal
        openOrderSuccess({
          symbol: order.token,
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          positionIdx: order.positionIdx,
          isSuccess: false,
          errorMessage: cleanedMessage
        });
      }

      // Immediately fetch the latest status for the new SL order
      if (placedOrderId) {
        try {
          await fetch(`${BASE_URL}/api/orders/status/${placedOrderId}`, { credentials: 'include' });
        } catch (e) {
          // ignore error, just try to update status
        }
      }
      fetchOrders();
      setOrdersTableRefreshKey(prev => prev + 1);
      await fetchSlOrders(order._id); // Refresh SL orders to update quantities
      setSlForm({ qty: '', price: '', orderType: 'Market' }); // Clear form
      setQtyValidationMessage(''); // Clear validation message on success
    } catch (err) {
      console.error(err);
      toast.error('Failed to place Stop Loss order', { position: 'top-right', autoClose: 5000 });
      // Show order failure modal for catch errors
      openOrderSuccess({
        symbol: order.token,
        side: order.side === 'Buy' ? 'Sell' : 'Buy',
        positionIdx: order.positionIdx,
        isSuccess: false,
        errorMessage: 'Failed to place Stop Loss order'
      });
    } finally {
      setSlLoading(false);
    }
  };

  // Copy functions for different values
  const handleCopyAvailable = () => {
    if (maxQty !== undefined && maxQty !== null) {
      navigator.clipboard.writeText(maxQty.toString());
      toast.success('Available Quantity Copied!', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleCopyCurrentPrice = () => {
    if (livePrice) {
      navigator.clipboard.writeText(livePrice.toString());
      toast.success('Current Price Copied!', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleCopyOnHold = () => {
    if (onHold !== undefined && onHold !== null) {
      navigator.clipboard.writeText(onHold.toString());
      toast.success('Quantity on Hold Copied!', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleCopySold = () => {
    if (sold !== undefined && sold !== null) {
      navigator.clipboard.writeText(sold.toString());
      toast.success('Sold Quantity Copied!', { position: 'top-right', autoClose: 1500 });
    }
  };

  if (!visible || !order) return null;

  const infoRows = [
    { label: 'Filled Quantity', value: order.filledQuantity || order.count || '-' },
    { label: 'Entry Price', value: order.entryPrice || '-' },
    { 
      label: 'Current Price', 
      value: livePrice || '-',
      showCopy: true,
      onCopy: handleCopyCurrentPrice
    },
    { 
      label: 'Quantity on hold', 
      value: onHold,
      showCopy: true,
      onCopy: handleCopyOnHold
    },
    { 
      label: 'Sold quantity', 
      value: sold,
      showCopy: true,
      onCopy: handleCopySold
    },
  ];

  return (
    <>
      {/* Main popup */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '98vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            width: '99%',
            maxWidth: 800,
            minWidth: 400,
            padding: 32,
            maxHeight: '92vh',
            overflowY: 'auto',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
            fontFamily: 'Roboto, Segoe UI, Arial, sans-serif',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 10, color: '#222', textAlign: 'center', letterSpacing: 0.5 }}>Stop Loss</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {infoRows.map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
                <span style={{ color: '#868484ff', fontWeight: 350 }}>{row.label}</span>
                <span style={{ color: '#222', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {row.value}
                  {row.showCopy && (
                    <span
                      onClick={row.onCopy}
                      style={{ cursor: 'pointer', marginLeft: 4, color: '#555', fontSize: 17, display: 'inline-flex', alignItems: 'center' }}
                      title="Copy"
                    >
                      <CopyOutlined />
                    </span>
                  )}
                </span>
              </div>
            ))}
            {/* Available Quantity row with copy icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
              <span style={{ color: '#868484ff', fontWeight: 350 }}>Available Quantity</span>
              <span style={{ color: '#222', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                {maxQty}
                <span
                  onClick={handleCopyAvailable}
                  style={{ cursor: 'pointer', marginLeft: 4, color: '#555', fontSize: 17, display: 'inline-flex', alignItems: 'center' }}
                  title="Copy"
                >
                  <CopyOutlined />
                </span>
              </span>
            </div>
          </div>

          {/* Close All Orders Button */}
          {/* <button
            onClick={() => setShowConfirmation(true)}
            style={{
              background: 'red',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '7px 0',
              fontWeight: 600,
              fontSize: 14,
              width: 70,
              minWidth: 50,
              cursor: 'pointer',
              marginRight: 0
            }}
          >
            Close
          </button> */}

          <div style={{ fontWeight: 600, fontSize: 16, margin: '18px 0 8px 0', color: '#222' }}>Stop loss order</div>
          {qtyValidationMessage && (
            <div style={{ 
              background: '#fff2f0', 
              border: '1px solid #ffccc7', 
              borderRadius: 4, 
              padding: '8px 12px', 
              marginBottom: 10, 
              color: '#cf1322', 
              fontSize: 14, 
              fontWeight: 500 
            }}>
              {qtyValidationMessage}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <input
              name="qty"
              type="number"
              placeholder="Quantity"
              value={slForm.qty}
              onChange={handleSlInput}
              min={0.0001}
              max={maxQty}
              style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
              autoComplete="off"
              autoFill="off"
            />
            <input
              name="price"
              type="number"
              placeholder="Price"
              value={slForm.price}
              onChange={handleSlInput}
              style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
              autoComplete="off"
              autoFill="off"
            />
          </div>
          <button
            onClick={handleSlSubmit}
            disabled={slLoading || !slForm.qty || !slForm.price}
            style={{
              background: 'black',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '10px 0',
              fontWeight: 600,
              fontSize: 16,
              cursor: slLoading ? 'not-allowed' : 'pointer',
              opacity: slLoading ? 0.7 : 1,
              transition: 'opacity 0.2s',
              marginBottom: 10,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            {slLoading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SpinnerSmall size={16} color="#fff" />
                <span style={{ fontWeight: 700 }}>Placing...</span>
              </span>
            ) : 'Submit'}
          </button>

          {/* Show SL orders using OrdersTable component */}
          {order && order._id && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 500, fontSize: 15, color: '#222', marginBottom: 4 }}>Already placed SL orders:</div>
              <OrdersTable orderID={order._id} orderType="sl" refreshKey={ordersTableRefreshKey}
               onOrderUpdate={() => {
                // When an order is cancelled, update quantities immediately
                fetchOrders();
                fetchSlOrders(order._id);
              }} />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Popup */}
      {showConfirmation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div
            style={{
              background: 'white',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              fontFamily: 'Roboto, Segoe UI, Arial, sans-serif',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16, color: '#222', textAlign: 'center' }}>
              Confirm Order Cancellation
            </div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 20, textAlign: 'center' }}>
              Are you sure you want to cancel all related orders? This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  background: '#f0f0f0',
                  color: '#222',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  minWidth: 80,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCloseAllOrders}
                style={{
                  background: 'red',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  minWidth: 80,
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StopLossPopup;


// import React, { useState, useEffect } from 'react';
// import { toast } from 'react-toastify';
// import { BASE_URL } from '../api';
// import OrdersTable from './OrdersTable';
// import { CopyOutlined } from '@ant-design/icons';



// const StopLossPopup = ({ visible, order, currentPrice, onClose, fetchOrders }) => {
//   const [slForm, setSlForm] = useState({ qty: '', price: '', orderType: 'Market' });
//   const [slLoading, setSlLoading] = useState(false);
//   const [livePrice, setLivePrice] = useState(currentPrice || '');
//   const [slOrders, setSlOrders] = useState([]);
//   const [ordersTableRefreshKey, setOrdersTableRefreshKey] = useState(0);

//   useEffect(() => {
//     if (visible && order && order._id) fetchSlOrders(order._id);
//   }, [visible, order]);

//   useEffect(() => {
//     let interval;
//     async function fetchPrice() {
//       if (order && order.token) {
//         try {
//           const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${order.token}`);
//           const data = await res.json();
//           setLivePrice(data?.result?.list?.[0]?.markPrice || '');
//         } catch {
//           setLivePrice('');
//         }
//       }
//     }
//     if (visible && order && order.token) {
//       fetchPrice();
//       interval = setInterval(fetchPrice, 1000);
//     }
//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [visible, order]);

//   const fetchSlOrders = async (LinkedOrderId) => {
//     try {
//       const res = await fetch(`${BASE_URL}/api/orders/sl/${LinkedOrderId}`);
//       const data = await res.json();
//       setSlOrders(Array.isArray(data) ? data : []);
//     } catch (err) {
//       setSlOrders([]);
//     }
//   };

//   // Calculate Quantity on hold and Sold quantity
//   const getSlQuantities = () => {
//     let onHold = 0;
//     let sold = 0;
//     slOrders.forEach(sl => {
//       const status = (sl.orderStatus || '').toLowerCase();
//       const qty = parseFloat(sl.count) || 0;
//       if (status !== 'cancelled' && status !== 'deactivated') {
//         onHold += qty;
//       }
//       if (status === 'activated' || status === 'filled' || status === 'triggered') {
//         sold += qty;
//       }
//     });
//     return { onHold, sold };
//   };
//   const { onHold, sold } = getSlQuantities();
//   const filledQty = order ? (parseFloat(order.filledQuantity) || parseFloat(order.count) || 0) : 0;
//   const maxQty = Math.max(0, filledQty - onHold - sold);

//   // Handle close all orders
//   const handleCloseAllOrders = async () => {
//     if (!order || !order._id) return;
//     try {
//       const res = await fetch(`${BASE_URL}/api/orders/cancel-all/${order._id}`, {
//         method: 'POST',
//       });
//       const data = await res.json();
//       if (data.success) {
//         toast.success('All related orders cancelled!', { position: 'top-right', autoClose: 5000 });
//         fetchOrders();
//         setOrdersTableRefreshKey(prev => prev + 1);
//         fetchSlOrders(order._id);
//         // onClose();
//       } else {
//         toast.error(data.error || 'Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
//       }
//     } catch (err) {
//       toast.error('Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
//     }
//   };

//   const handleSlInput = (e) => {
//     const { name, value } = e.target;
//     let val = value;
//     if (name === 'qty') {
//       if (val && (parseFloat(val) > maxQty)) {
//         toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
//         val = maxQty;
//       }
//     }
//     setSlForm(prev => ({ ...prev, [name]: val }));
//   };

//   const handleSlSubmit = async () => {
//     if (!slForm.qty || !slForm.price) return;
//     if (parseFloat(slForm.qty) > maxQty) {
//       toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
//       return;
//     }
//     setSlLoading(true);
//     try {
//       const res = await fetch(`${BASE_URL}/api/orders/place`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           token: order.token,
//           count: slForm.qty,
//           orderType: 'Market',
//           side: order.side === 'Buy' ? 'Sell' : 'Buy',
//           signalBy: order.signalBy,
//           price: slForm.price,
//           LinkedOrderId: order._id,
//           orderNature: 'sl',
//           positionIdx: order.positionIdx,
//           stopLoss: slForm.price,
//         }),
//       });
//       const data = await res.json();
//       if (data.bybitRes && data.bybitRes.retMsg) {
//         if (data.bybitRes.retMsg === 'OK') {
//           toast.success('Stop Loss order placed successfully!', { position: 'top-right', autoClose: 5000 });
//            fetchOrders();
//            setOrdersTableRefreshKey(prev => prev + 1);
//            fetchSlOrders(order._id);
//         } else {
//           toast.error(data.bybitRes.retMsg || 'Stop Loss order failed!', { position: 'top-right', autoClose: 5000 });
//         }
//       } else if (data.success) {
//         toast.success('Stop Loss order placed successfully!', { position: 'top-right', autoClose: 5000 });
//       } else {
//         toast.error(data.error || 'Stop Loss order failed!', { position: 'top-right', autoClose: 5000 });
//       }
//       fetchOrders();
//       setOrdersTableRefreshKey(prev => prev + 1);
//       fetchSlOrders(order._id);
//       // onClose();
//     } catch (err) {
//       console.error(err);
//       toast.error('Failed to place Stop Loss order', { position: 'top-right', autoClose: 5000 });
//     } finally {
//       setSlLoading(false);
//     }
//   };

//   if (!visible || !order) return null;

//   const infoRows = [
//     { label: 'Filled Quantity', value: order.filledQuantity || order.count || '-' },
//     { label: 'Entry Price', value: order.entryPrice || '-' },
//     { label: 'Current Price', value: livePrice || '-' },
//     { label: 'Quantity on hold', value: onHold },
//     { label: 'Sold quantity', value: sold },
//       // { label: 'Available Quantity', value: maxQty }, // <-- Add this line

//   ];

//    // Copy available quantity to clipboard
//     const handleCopyAvailable = () => {
//       if (maxQty !== undefined && maxQty !== null) {
//         navigator.clipboard.writeText(maxQty.toString());
//         toast.success('Copied!', { position: 'top-right', autoClose: 1500 });
//       }
//     };

//   return (
//     <div
//       style={{
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         width: '98vw',
//         height: '100vh',
//         background: 'rgba(0,0,0,0.25)',
//         zIndex: 1000,
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//       }}
//       onClick={onClose}
//     >
//       <div
//         style={{
//           background: 'white',
//           border: '1px solid #d9d9d9',
//           borderRadius: 8,
//           width: '99%',
//           maxWidth: 800,
//           minWidth: 400,
//           padding: 32,
//           maxHeight: '92vh',
//           overflowY: 'auto',
//           boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
//           position: 'relative',
//           display: 'flex',
//           flexDirection: 'column',
//           gap: 22,
//           fontFamily: 'Roboto, Segoe UI, Arial, sans-serif',
//         }}
//         onClick={e => e.stopPropagation()}
//       >
//         <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 10, color: '#222', textAlign: 'center', letterSpacing: 0.5 }}>Stop Loss</div>
//         <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
//           {infoRows.map(row => (
//             <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
//               <span style={{ color: '#868484ff', fontWeight: 350 }}>{row.label}</span>
//               <span style={{ color: '#222', fontWeight: 600 }}>{row.value}</span>
//             </div>
//           ))}
//            {/* Available Quantity row with copy icon */}
//                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
//                       <span style={{ color: '#868484ff', fontWeight: 350 }}>Available Quantity</span>
//                       <span style={{ color: '#222', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
//                         {maxQty}
//                         <span
//                           onClick={handleCopyAvailable}
//                           style={{ cursor: 'pointer', marginLeft: 4, color: '#555', fontSize: 17, display: 'inline-flex', alignItems: 'center' }}
//                           title="Copy"
//                         >
//                           <CopyOutlined />
//                         </span>
//                       </span>
//                     </div>
//         </div>
//         {/* Close All Orders Button */}
//         {/* <button
//           onClick={handleCloseAllOrders}
//           style={{
//             background: 'red',
//             color: 'white',
//             border: 'none',
//             borderRadius: 4,
//             padding: '10px 0',
//             fontWeight: 600,
//             fontSize: 16,
//             margin: '12px 0 18px 0',
//             cursor: 'pointer',
//             width: '100%'
//           }}
//         >
//           Close
//         </button> */}
//          <button
//     onClick={handleCloseAllOrders}
//     style={{
//       background: 'red',
//       color: 'white',
//       border: 'none',
//       borderRadius: 4,
//       padding: '7px 0',
//       fontWeight: 600,
//       fontSize: 14,
//       width: 70,
//       minWidth: 50,
//       cursor: 'pointer',
//       marginRight: 0
//     }}
//   >
//     Close
//   </button>
//         <div style={{ fontWeight: 600, fontSize: 16, margin: '18px 0 8px 0', color: '#222' }}>Stop loss order</div>
//         <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
//           <input
//             name="qty"
//             type="number"
//             placeholder="Quantity"
//             value={slForm.qty}
//             onChange={handleSlInput}
//             min={0.0001}
//             max={maxQty}
//             style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
//           />
//           <input
//             name="price"
//             type="number"
//             placeholder="Price"
//             value={slForm.price}
//             onChange={handleSlInput}
//             style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
//           />
//         </div>
//         <button
//           onClick={handleSlSubmit}
//           disabled={slLoading || !slForm.qty || !slForm.price}
//           style={{
//             background: 'black',
//             color: 'white',
//             border: 'none',
//             borderRadius: 4,
//             padding: '10px 0',
//             fontWeight: 600,
//             fontSize: 16,
//             cursor: slLoading ? 'not-allowed' : 'pointer',
//             opacity: slLoading ? 0.7 : 1,
//             transition: 'opacity 0.2s',
//             marginBottom: 10,
//           }}
//         >
//           {slLoading ? (
//             <span style={{ 
//               display: 'inline-block', 
//               width: 18, 
//               height: 18, 
//               border: '2px solid #fff', 
//               borderTop: '2px solid #222', 
//               borderRadius: '50%', 
//               animation: 'spin 1s linear infinite' 
//             }} />
//           ) : 'Submit'}
//         </button>
//         {/* Show SL orders using OrdersTable component */}
//         {order && order._id && (
//           <div style={{ marginTop: 8 }}>
//             <div style={{ fontWeight: 500, fontSize: 15, color: '#222', marginBottom: 4 }}>Already placed SL orders:</div>
//             <OrdersTable orderID={order._id} orderType="sl" refreshKey={ordersTableRefreshKey} />
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default StopLossPopup;